import { FormInput, FormLabel, Form, Text, FormFieldGroup, Button, Alert, SocialLoginButton } from '$components'
import { GoogleIcon, LogoIcon, FacebookIcon } from '$icons'
import { ElementIntent } from '$enums'
import * as z from 'zod'
import { useAuth } from '../hooks/use-auth'
import { Link } from 'react-router-dom'
import { AuthRoutePath } from '../routes/public'
import { OnboardingRoutePath } from '$features/onboarding/routes/public'
import { usePageTitle } from '$components'

enum Input {
  Email = 'email',
  Password = 'password',
}

const schema = z.object({
  [Input.Email]: z.string().min(1, 'Email is required').email().trim(),
  [Input.Password]: z.string().min(1, 'Password is required').trim(),
})

type FormValues = z.infer<typeof schema>

export function LoginPage () {
  usePageTitle('Login')
  const { signIn, signInOAuth, isSignInLoading, error } = useAuth()

  const onSubmit = async (values: FormValues) => signIn(values)

  return (
    <div className="flex min-h-screen w-full">
      {/* Login form */}
      <div className="flex w-full items-center justify-center px-stack-section">
        <div className="w-full max-w-prose-sm space-y-stack-section pb-stack-section">
          {/* Logo */}
          <div className="mb-stack-hero pt-stack-section">
            <LogoIcon className="h-6 w-auto" />
          </div>

          <div className="space-y-stack-tight">
            <Text
              type="h1"
              as="h1"
            >
              Login
            </Text>

            <Text className="text-fg-subtle">
              Welcome back! Sign in to access your account.
            </Text>
          </div>

          <div className="flex flex-col gap-stack-tight">
            <SocialLoginButton
              icon={<GoogleIcon />}
              onClick={() => signInOAuth('google')}
              disabled={isSignInLoading}
            >
              Continue with Google
            </SocialLoginButton>

            <SocialLoginButton
              icon={<FacebookIcon />}
              onClick={() => signInOAuth('facebook')}
              disabled={isSignInLoading}
            >
              Continue with Facebook
            </SocialLoginButton>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border-strong" />
            </div>
            <div className="relative flex justify-center text-caption uppercase">
              <Text
                type="xs"
                className="bg-surface px-stack-tight"
              >
                Or continue with email
              </Text>
            </div>
          </div>

          <Form
            onSubmit={onSubmit}
            schema={schema}
          >
            {() => (
              <div className="flex flex-col gap-stack-roomy">
                <FormFieldGroup>
                  <FormLabel htmlFor={Input.Email}>
                    Email
                  </FormLabel>

                  <FormInput name={Input.Email} />
                </FormFieldGroup>

                <FormFieldGroup>
                  <div className="flex items-center">
                    <FormLabel htmlFor={Input.Password}>Password</FormLabel>

                    <Text
                      type="s"
                      className="ml-auto underline"
                    >
                      <Link to={AuthRoutePath.ForgotPassword}>
                        Forgot your password?
                      </Link>
                    </Text>
                  </div>

                  <FormInput
                    name={Input.Password}
                    type="password"
                  />
                </FormFieldGroup>

                <div className="mt-stack-roomy">
                  <Button
                    type="submit"
                    disabled={isSignInLoading}
                    isLoading={isSignInLoading}
                    className="w-full"
                    size="lg"
                  >
                    Sign in
                  </Button>
                </div>

                <Text
                  type="s"
                  className="text-center"
                >
                  Don&apos;t have an account?&nbsp;

                  <Link
                    to={OnboardingRoutePath.Register}
                    className="underline"
                  >
                    Sign up here
                  </Link>
                </Text>

                { error && (
                  <Alert intent={ElementIntent.Danger}>
                    <Alert.Heading>
                      Unable to sign in
                    </Alert.Heading>

                    <Alert.Body>
                      { error.message }
                    </Alert.Body>
                  </Alert>
                )}
              </div>
            )}
          </Form>
        </div>
      </div>

      {/* Image for desktop */}
      {/* <div className="hidden w-2/3 bg-surface lg:block p-stack-section">
        <img
          src="https://placehold.co/1920x1080"
          alt="Login background"
          width={1920}
          height={1080}
          className="h-full w-full object-cover rounded-card"
        />
      </div> */}
    </div>
  )
}
